import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Scenario, Session } from "../types";
import { getScenario } from "../utils/apiUtil";
import { personaHueByIndex } from "./PersonaHue";
import MarkdownMessage from "./MarkdownMessage";
import PersonaAvatar, { initialsOf } from "./PersonaAvatar";
import NotFoundPage from "./NotFoundPage";
import { categoryStyle } from "../utils/categoryStyles";
import { hasSessionForScenario } from "../utils/sessionLookup";

interface ScenarioBriefingPageProps {
  getToken: () => string | undefined;
  onBeginScenario: (scenarioId: string) => Promise<void>;
  /**
   * The caller's own sessions. The page is reachable from the card body at any
   * time, including mid-scenario, so it has to know whether this scenario is
   * already under way — otherwise its CTA would silently start a second
   * session over the top of one the player is in.
   */
  sessions: Session[];
  onResumeScenario: (scenarioId: string) => void;
}

/**
 * Casts are small — two to four for most scenarios, seven at the outside — so
 * the count reads as a word. Falls back to the numeral above that rather than
 * growing a spelling table nobody will ever reach.
 */
const NUMBER_WORDS = [
  "Zero", "One", "Two", "Three", "Four", "Five", "Six",
  "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve",
];
const castWord = (n: number): string => NUMBER_WORDS[n] ?? String(n);

// The scrims carry no runtime value but several stops each, so they live here
// as background images rather than as unreadable Tailwind arbitrary values.
// `#0b1326` is the `background` token — the theme is declared in Tailwind's JS
// config in web/index.html, not as CSS custom properties, so there is no var()
// to reference. Change these together with that token.
const HERO_SCRIM: React.CSSProperties = {
  backgroundImage:
    "linear-gradient(to top, #0b1326 0%, rgba(11,19,38,.84) 26%, rgba(11,19,38,.14) 64%, rgba(11,19,38,.34) 100%)," +
    "linear-gradient(to right, rgba(11,19,38,.72) 0%, rgba(11,19,38,.08) 56%)",
};
const HERO_FALLBACK_SCRIM: React.CSSProperties = {
  backgroundImage:
    "linear-gradient(to top, #0b1326 0%, rgba(11,19,38,.6) 42%, transparent 100%)",
};
// The library card's graph-paper texture at page scale. Deliberately identical
// to the band in SessionToolColumn's ScenarioView: a scenario with no hero art
// is the same absence on both surfaces and must look the same on both.
const HERO_GRID: React.CSSProperties = {
  backgroundImage:
    "repeating-linear-gradient(0deg,transparent,transparent 23px,currentColor 23px,currentColor 24px)," +
    "repeating-linear-gradient(90deg,transparent,transparent 23px,currentColor 23px,currentColor 24px)",
};

const FOCUS_RING =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tertiary";

/** A label plus a hairline. Teal, because a section rule is product chrome. */
const SectionRule: React.FC<{ label: string; count?: string; spaced?: boolean }> = ({
  label,
  count,
  spaced,
}) => (
  <div className={`flex items-center gap-4 mb-[18px] ${spaced ? "mt-[38px]" : ""}`}>
    <span className="text-[10.5px] font-label font-bold text-primary tracking-[0.19em] uppercase whitespace-nowrap">
      {label}
    </span>
    <span className="flex-1 h-px bg-primary/25" />
    {count && (
      <span className="text-[10.5px] font-label font-semibold text-slate-500 tracking-[0.19em] uppercase whitespace-nowrap">
        {count}
      </span>
    )}
  </div>
);

const RailBlock: React.FC<{ label: string; text: string }> = ({ label, text }) => (
  <div>
    <p className="text-[10px] font-label font-semibold text-slate-500 tracking-[0.17em] uppercase m-0 mb-2">
      {label}
    </p>
    {/* "compact" deliberately drops remark-breaks: this copy is hand-wrapped
        Markdown in YAML, and honouring those newlines leaves a ragged right
        edge in a 292px rail. */}
    <div className="text-[14.5px] leading-[1.52] text-on-surface">
      <MarkdownMessage text={text} variant="compact" />
    </div>
  </div>
);

/**
 * The page's single commitment. "Resume" is not a second style of button —
 * it is the same commitment pointed at a session that already exists, so it
 * keeps the same weight and position and only changes its word.
 */
const BeginButton: React.FC<{
  busy: boolean;
  resume: boolean;
  onClick: () => void;
}> = ({ busy, resume, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    className={`w-full py-3 px-6 rounded font-label font-bold text-xs uppercase tracking-[0.1em] bg-primary text-on-primary transition-opacity duration-200 motion-reduce:transition-none hover:opacity-90 active:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed ${FOCUS_RING}`}
  >
    {busy ? "Starting..." : resume ? "Resume Scenario" : "Begin Scenario"}
  </button>
);

/**
 * The pre-session briefing: the gate between the library and the chat. Every
 * section is independently optional — a scenario with no hero art, no role, no
 * objective and no briefing still has to read as a deliberate page, because
 * that is the state both seeded scenarios shipped in before this round.
 *
 * Documents are never listed here. They are discovered through play, and
 * counting them pre-empts one scenario and spoils the other.
 */
const ScenarioBriefingPage: React.FC<ScenarioBriefingPageProps> = ({
  getToken,
  onBeginScenario,
  sessions,
  onResumeScenario,
}) => {
  const { scenarioId, orgSlug } = useParams<{ scenarioId: string; orgSlug: string }>();
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);

  // Hero and logo are short-lived signed GCS URLs and a page left open outlives
  // one, so a failed load falls through to the same fallback as "never had art"
  // rather than showing a broken-image box.
  const [heroFailed, setHeroFailed] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setHeroFailed(false);
    setLogoFailed(false);
    getScenario(scenarioId ?? "", getToken())
      .then((s) => {
        if (!cancelled) setScenario(s);
      })
      .catch((e) => {
        // A scenario this caller cannot read 404s deliberately, so there is
        // nothing worth distinguishing here: both are "not available to you".
        console.error("Failed to load scenario", e);
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scenarioId]);

  const backLink = (
    <Link
      to={`/${orgSlug}/library`}
      className={`inline-flex items-center gap-1.5 rounded-sm text-[10.5px] font-label font-semibold tracking-[0.14em] uppercase text-slate-400 hover:text-on-surface transition-colors motion-reduce:transition-none ${FOCUS_RING}`}
    >
      <span className="material-symbols-outlined text-[15px]">arrow_back</span>
      Scenario Library
    </Link>
  );

  if (notFound) return <NotFoundPage />;

  // Loading renders the page's own chrome with neutral blocks in it, so the
  // layout settles rather than jumping when the content lands.
  if (loading || !scenario) {
    return (
      <div className="flex flex-col flex-1 h-full min-w-0 overflow-y-auto bg-background">
        <div className="relative isolate flex flex-col justify-between h-[178px] md:h-[276px] shrink-0">
          <div className="absolute inset-0 -z-30 bg-surface-container-low" />
          <div className="absolute inset-0 -z-20 bg-gradient-to-br from-primary/20 via-primary/5 to-transparent" />
          <div className="absolute inset-0 -z-10 opacity-5 text-on-surface" style={HERO_GRID} />
          <div className="absolute inset-0" style={HERO_FALLBACK_SCRIM} />
          <div className="relative px-[18px] md:px-10 pt-[18px]">{backLink}</div>
          <div className="relative px-[18px] md:px-10 pb-5 md:pb-[26px]">
            <div className="h-8 md:h-11 w-[min(20ch,80%)] rounded bg-on-surface/10 animate-pulse motion-reduce:animate-none" />
          </div>
        </div>
        <div className="px-[18px] md:px-10 py-8 space-y-3">
          <div className="h-3 w-40 rounded bg-on-surface/10 animate-pulse motion-reduce:animate-none" />
          <div className="h-3 w-full max-w-[62ch] rounded bg-on-surface/5 animate-pulse motion-reduce:animate-none" />
          <div className="h-3 w-4/5 max-w-[62ch] rounded bg-on-surface/5 animate-pulse motion-reduce:animate-none" />
        </div>
      </div>
    );
  }

  const showHero = !!scenario.hero && !heroFailed;
  const showLogo = !!scenario.logo && !logoFailed;
  const personas = scenario.personas ?? [];
  const hasAssignment = !!(scenario.playerRole || scenario.objective);
  // A draft is readable by its author/managers but never sessionable — the
  // library never links here for one (the draft tile goes to /edit), so the
  // only way in is a typed URL. Render the page for context but withhold the
  // commitment rather than let a manual link start a real session against
  // unpublished content.
  const isPublished = scenario.status === "published";

  // A player who already has a session here reached this page to re-read the
  // briefing, not to start over. Resuming is a plain navigation, so it never
  // touches `busy` — only session creation can be in flight.
  const resume = hasSessionForScenario(sessions, scenario.id);

  const begin = () => {
    if (busy) return;
    if (resume) {
      onResumeScenario(scenario.id);
      return;
    }
    setBusy(true);
    // onBeginScenario creates the session and navigates on success, and
    // surfaces its own failure at the app level — this page only has to let
    // the button go again.
    void onBeginScenario(scenario.id).finally(() => setBusy(false));
  };

  return (
    <div className="flex flex-col flex-1 h-full min-w-0 overflow-y-auto bg-background">
      {/* ---------------------------- Hero ---------------------------- */}
      <div className="relative isolate flex flex-col justify-between h-[178px] md:h-[276px] shrink-0">
        {showHero ? (
          <>
            <img
              src={scenario.hero}
              alt=""
              onError={() => setHeroFailed(true)}
              className="absolute inset-0 w-full h-full object-cover -z-30"
            />
            <div className="absolute inset-0 -z-20 bg-surface-container-lowest opacity-35 mix-blend-multiply" />
            <div className="absolute inset-0 -z-10" style={HERO_SCRIM} />
          </>
        ) : (
          <>
            <div className="absolute inset-0 -z-30 bg-surface-container-low" />
            <div className="absolute inset-0 -z-20 bg-gradient-to-br from-primary/20 via-primary/5 to-transparent" />
            <div className="absolute inset-0 -z-10 opacity-5 text-on-surface" style={HERO_GRID} />
            <div className="absolute inset-0" style={HERO_FALLBACK_SCRIM} />
          </>
        )}

        <div className="relative px-[18px] md:px-10 pt-[18px]">{backLink}</div>

        <div className="relative flex items-end gap-3 md:gap-[17px] px-[18px] md:px-10 pb-5 md:pb-[26px]">
          {showLogo ? (
            <img
              src={scenario.logo}
              alt=""
              onError={() => setLogoFailed(true)}
              className="w-10 h-10 md:w-[54px] md:h-[54px] shrink-0 rounded-[7px] object-cover border border-on-surface/15"
            />
          ) : (
            <div className="grid place-items-center w-10 h-10 md:w-[54px] md:h-[54px] shrink-0 rounded-[7px] bg-surface-container-high border border-on-surface/15 font-headline font-bold text-[13px] md:text-[16px] text-primary">
              {initialsOf(scenario.title)}
            </div>
          )}
          <div className="min-w-0">
            {/* Tinted per category, same as the library card's badge — the
                hero wash below stays teal regardless (it matches the chat
                Scenario pane's fallback gradient), so only the badge varies. */}
            {scenario.category && (
              <span
                className={`inline-block mb-[11px] px-[9px] py-1 rounded border backdrop-blur-sm text-[10px] font-label font-bold tracking-[0.16em] uppercase ${categoryStyle(scenario.category).badge}`}
              >
                {scenario.category}
              </span>
            )}
            <h1 className="font-headline font-bold text-[1.45rem] md:text-[clamp(2rem,3.3vw,2.75rem)] leading-[1.05] tracking-[-0.026em] text-white text-balance max-w-[20ch] m-0">
              {scenario.title}
            </h1>
          </div>
        </div>
      </div>

      {/* --------------------- Split: rail + material --------------------- */}
      {/* `grow` so the rail column reaches the bottom of the viewport on a
          short cast, rather than stopping mid-page over bare background. */}
      <div className="grow flex flex-col md:flex-row md:items-start">
        {/* The column carries the panel treatment so it runs full height; the
            rail inside it is what actually sticks. Below md it unrolls, loses
            the fixed width, stops sticking, and trades its right border for a
            bottom one. With neither role nor objective there is nothing to
            unroll, so it collapses entirely on narrow — the Begin button lives
            in the bottom bar there anyway. */}
        <div
          className={`w-full md:w-[360px] shrink-0 self-stretch bg-surface-container-lowest border-b md:border-b-0 md:border-r border-outline-variant/40 ${
            hasAssignment ? "" : "hidden md:block"
          }`}
        >
          <div className="static md:sticky md:top-0 flex flex-col gap-[22px] px-[18px] py-5 md:px-6 md:pt-[26px] md:pb-6">
            {scenario.playerRole && <RailBlock label="Your role" text={scenario.playerRole} />}
            {scenario.playerRole && scenario.objective && (
              <div className="h-px bg-outline-variant/50" />
            )}
            {scenario.objective && <RailBlock label="Your objective" text={scenario.objective} />}
            {/* Below md this is replaced by the sticky bottom bar. */}
            <div className="hidden md:block">
              {isPublished ? (
                <>
                  <BeginButton busy={busy} resume={resume} onClick={begin} />
                  {personas.length > 0 && (
                    <p className="mt-2.5 m-0 text-[10.5px] font-label text-slate-500 text-center">
                      {castWord(personas.length)} profiles
                    </p>
                  )}
                </>
              ) : (
                <p className="m-0 py-3 text-[12px] font-label text-slate-500 text-center">
                  This scenario is not published yet.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0 px-[18px] pt-[22px] pb-7 md:px-10 md:pt-7 md:pb-11">
          <SectionRule label="Scenario" />
          {/* Falls back to `description` so the section is never empty on a
              scenario authored before `briefing` existed. */}
          <div className="max-w-[70ch] text-[14.5px] leading-[1.72] text-slate-400 [&_p]:mb-3.5">
            <MarkdownMessage
              text={scenario.briefing || scenario.description || ""}
              variant="compact"
            />
          </div>

          {personas.length > 0 && (
            <>
              <SectionRule label="Persona Profiles" count={castWord(personas.length)} spaced />
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-10">
                {personas.map((p, i) => {
                  const hue = personaHueByIndex(i);
                  return (
                    <div
                      key={p.id}
                      className="flex gap-4 md:gap-5 py-5 border-b border-outline-variant/25 last:border-b-0 xl:[&:nth-last-child(2):nth-child(odd)]:border-b-0"
                    >
                      <PersonaAvatar
                        name={p.name}
                        src={p.avatar}
                        alt=""
                        className="w-[62px] h-[62px] md:w-[88px] md:h-[88px] shrink-0 rounded-md border-2"
                        textClassName="font-headline text-[18px] md:text-[25px]"
                        style={{
                          borderColor: `color-mix(in srgb, ${hue} 42%, transparent)`,
                        }}
                        // A persona with no usable portrait gets the tile
                        // tinted rather than left grey — the tile IS the
                        // portrait for them, so this is still the same two
                        // touches of hue, not a third. Same 13% fill and 34%
                        // border as PersonaInitials in SessionToolColumn, so
                        // the same person is the same colour on both surfaces.
                        // `#222a3d` mirrors `surface-container-high`: the theme
                        // lives in Tailwind's JS config (web/index.html), not in
                        // CSS custom properties, so color-mix has no var() to
                        // reference. Change both together.
                        fallbackStyle={{
                          color: hue,
                          background: `color-mix(in srgb, ${hue} 13%, #222a3d)`,
                          borderColor: `color-mix(in srgb, ${hue} 34%, transparent)`,
                        }}
                      />
                      <div className="min-w-0 pt-0.5">
                        <h3 className="font-headline font-bold text-base md:text-[19px] tracking-[-0.01em] text-on-surface m-0 mb-1">
                          {p.name}
                        </h3>
                        {/* Two touches of hue per row and no more: the role label
                            and the portrait edge. Teal stays with the CTA and the
                            section rules, so the call to action never competes
                            with decoration. */}
                        <p
                          className="text-[10px] font-label font-semibold tracking-[0.15em] uppercase m-0 mb-2"
                          style={{ color: hue }}
                        >
                          {p.role}
                        </p>
                        {p.description && (
                          <p className="text-[13px] leading-relaxed text-slate-400 m-0">
                            {p.description}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Narrow only: the rail's button is hidden there, so the commitment
          lives in a bar that stays on screen through the whole roster. */}
      <div className="md:hidden sticky bottom-0 mt-auto py-3 px-[18px] bg-surface-container-lowest/95 backdrop-blur border-t border-outline-variant/40">
        {isPublished ? (
          <BeginButton busy={busy} resume={resume} onClick={begin} />
        ) : (
          <p className="m-0 text-[12px] font-label text-slate-500 text-center">
            This scenario is not published yet.
          </p>
        )}
      </div>
    </div>
  );
};

export default ScenarioBriefingPage;
